/**
 * Hook that manages all form state for the settings page.
 *
 * Holds local edits for all sections. The single "Update" button
 * calls getUpdates() to collect everything and save in one request.
 */
import { useState, useEffect, useCallback } from 'react';
import { useQuicksightDashboardField } from './useQuicksightDashboardField';
import { useNetworkLinkDefaultsField } from './useNetworkLinkDefaultsField';
import { useWellTestOverviewColumns } from './useWellTestOverviewColumns';
import { useMeterAllocationConfig } from './useMeterAllocationConfig';
import { useWellTestManagementColumns } from './useWellTestManagementColumns';
import { useOilInventoryAttributesField } from './useOilInventoryAttributesField';
import { useDuplicateTicketBehaviorField } from './useDuplicateTicketBehaviorField';
import { useTicketOverviewColumns } from './useTicketOverviewColumns';
import { usePasswordComplexityField } from './usePasswordComplexityField';
// @ts-ignore — CommonJS module from @scoutfdc/common
const { network_links: NETWORK_LINKS_FEATURE_CODE, quicksight: QUICKSIGHT_FEATURE_CODE, well_tests: WELL_TESTS_FEATURE_CODE, well_test_management: WELL_TEST_MANAGEMENT_FEATURE_CODE, oil_sales_and_inventory_queue: OIL_SALES_AND_INVENTORY_FEATURE_CODE, } = require('@scoutfdc/common/lib/features');
export function toIsoDate(value) {
    if (!value) {
        return '';
    }
    return value.slice(0, 10);
}
export function useSettingsForm(org, canEditAdvancedOptions = true) {
    const [days, setDays] = useState('');
    const [maxChars, setMaxChars] = useState('');
    const [calcCutoff, setCalcCutoff] = useState('');
    const [ticketClosingDate, setTicketClosingDate] = useState('');
    const [activeDate, setActiveDate] = useState('');
    const [inactiveDate, setInactiveDate] = useState('');
    const [billingStatus, setBillingStatus] = useState('');
    const [featureCodes, setFeatureCodes] = useState([]);
    const [dispositionCodes, setDispositionCodes] = useState([]);
    const [errors, setErrors] = useState({});
    const [initializedOrganizationId, setInitializedOrganizationId] = useState(null);
    const wellTestOverviewColumns = useWellTestOverviewColumns(org);
    const meterAllocationConfig = useMeterAllocationConfig(org);
    const oilInventoryAttributes = useOilInventoryAttributesField(org);
    const duplicateTicketBehavior = useDuplicateTicketBehaviorField(org);
    const ticketOverviewColumns = useTicketOverviewColumns(org);
    const passwordComplexity = usePasswordComplexityField(org);
    // --- new option fields go above this line ---
    const quicksightDashboard = useQuicksightDashboardField(org);
    const networkLinkDefaultsField = useNetworkLinkDefaultsField(org);
    const wellTestManagementEnabled = featureCodes.includes(WELL_TESTS_FEATURE_CODE) && featureCodes.includes(WELL_TEST_MANAGEMENT_FEATURE_CODE);
    const wellTestManagementColumns = useWellTestManagementColumns(wellTestManagementEnabled);
    const reset = useCallback(() => {
        if (!org) {
            setInitializedOrganizationId(null);
            return;
        }
        setDays(String(org.days_of_historical_edit || ''));
        setMaxChars(String(org.ticket_number_max_chars || ''));
        setCalcCutoff(toIsoDate(org.options && org.options.calc_cutoff));
        setTicketClosingDate(toIsoDate(org.ticket_closing_date));
        setActiveDate(toIsoDate(org.active_date));
        setInactiveDate(toIsoDate(org.inactive_date));
        setBillingStatus(org.billing_status || '');
        setFeatureCodes(org.feature_codes || []);
        setDispositionCodes(org.ticket_dispositions || []);
        setErrors({});
        quicksightDashboard.reset();
        networkLinkDefaultsField.reset();
        wellTestOverviewColumns.reset();
        meterAllocationConfig.reset();
        wellTestManagementColumns.reset();
        oilInventoryAttributes.reset();
        duplicateTicketBehavior.reset();
        ticketOverviewColumns.reset();
        passwordComplexity.reset();
        setInitializedOrganizationId(org.id);
    }, [org, quicksightDashboard.reset, networkLinkDefaultsField.reset, wellTestOverviewColumns.reset, meterAllocationConfig.reset, wellTestManagementColumns.reset, oilInventoryAttributes.reset, duplicateTicketBehavior.reset, ticketOverviewColumns.reset, passwordComplexity.reset]);
    useEffect(() => {
        reset();
    }, [reset]);
    const toggleFeature = useCallback((code, checked) => {
        setFeatureCodes((prev) => {
            const next = checked
                ? [...prev, code].sort((a, b) => a - b)
                : prev.filter((c) => c !== code);
            return next;
        });
    }, []);
    const toggleDisposition = useCallback((code, checked) => {
        setDispositionCodes((prev) => {
            const next = checked
                ? [...prev, code].sort((a, b) => a - b)
                : prev.filter((c) => c !== code);
            return next;
        });
    }, []);
    const validate = useCallback(() => {
        const newErrors = {};
        if (!days || Number(days) < 1) {
            newErrors.days = 'Must be a positive number';
        }
        if (!maxChars || Number(maxChars) < 1) {
            newErrors.maxChars = 'Must be a positive number';
        }
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        if (calcCutoff && calcCutoff > today) {
            newErrors.calcCutoff = 'Last Day To Calculate cannot be a future date.';
        }
        if (ticketClosingDate && ticketClosingDate > today) {
            newErrors.ticketClosingDate = 'Ticket Closing Date cannot be a future date.';
        }
        if (activeDate && activeDate > today) {
            newErrors.activeDate = 'Active Date cannot be a future date.';
        }
        if (inactiveDate && inactiveDate > today) {
            newErrors.inactiveDate = 'Inactive Date cannot be a future date.';
        }
        setErrors(newErrors);
        const wellTestOverviewValid = !featureCodes.includes(WELL_TESTS_FEATURE_CODE) || wellTestOverviewColumns.validate();
        const oilInventoryValid = !canEditAdvancedOptions || !featureCodes.includes(OIL_SALES_AND_INVENTORY_FEATURE_CODE) || oilInventoryAttributes.validate();
        const meterAllocationConfigValid = !canEditAdvancedOptions || !featureCodes.includes(WELL_TESTS_FEATURE_CODE) || meterAllocationConfig.validate();
        const ticketOverviewValid = !canEditAdvancedOptions || ticketOverviewColumns.validate();
        const passwordComplexityValid = !canEditAdvancedOptions || passwordComplexity.validate();
        return { valid: Object.keys(newErrors).length === 0 && wellTestOverviewValid && oilInventoryValid && meterAllocationConfigValid && ticketOverviewValid && passwordComplexityValid, errors: newErrors };
    }, [days, maxChars, calcCutoff, ticketClosingDate, activeDate, inactiveDate, featureCodes, canEditAdvancedOptions, wellTestOverviewColumns.validate, oilInventoryAttributes.validate, meterAllocationConfig.validate, ticketOverviewColumns.validate, passwordComplexity.validate]);
    const getUpdates = useCallback(() => {
        return {
            days_of_historical_edit: parseInt(days, 10),
            ticket_number_max_chars: parseInt(maxChars, 10),
            ticket_closing_date: ticketClosingDate || null,
            active_date: activeDate || null,
            inactive_date: inactiveDate || null,
            billing_status: billingStatus || null,
            options: {
                ...(org && org.options),
                calc_cutoff: calcCutoff || null,
                ...(canEditAdvancedOptions && featureCodes.includes(QUICKSIGHT_FEATURE_CODE) ? quicksightDashboard.toOptionsPatch() : {}),
                ...(canEditAdvancedOptions && featureCodes.includes(WELL_TESTS_FEATURE_CODE) ? wellTestOverviewColumns.toOptionsPatch() : {}),
                ...(canEditAdvancedOptions && featureCodes.includes(WELL_TESTS_FEATURE_CODE) ? meterAllocationConfig.toOptionsPatch() : {}),
                ...(canEditAdvancedOptions && featureCodes.includes(NETWORK_LINKS_FEATURE_CODE) ? networkLinkDefaultsField.toOptionsPatch() : {}),
                ...(canEditAdvancedOptions && featureCodes.includes(OIL_SALES_AND_INVENTORY_FEATURE_CODE) ? oilInventoryAttributes.toOptionsPatch() : {}),
                ...(canEditAdvancedOptions ? duplicateTicketBehavior.toOptionsPatch() : {}),
                ...(canEditAdvancedOptions ? ticketOverviewColumns.toOptionsPatch() : {}),
                ...(canEditAdvancedOptions ? passwordComplexity.toOptionsPatch() : {}),
            },
            feature_codes: featureCodes,
            ticket_dispositions: dispositionCodes,
        };
    }, [days, maxChars, ticketClosingDate, activeDate, inactiveDate, billingStatus, calcCutoff, featureCodes, dispositionCodes, org, canEditAdvancedOptions, quicksightDashboard.toOptionsPatch, wellTestOverviewColumns.toOptionsPatch, meterAllocationConfig.toOptionsPatch, networkLinkDefaultsField, oilInventoryAttributes.toOptionsPatch, duplicateTicketBehavior.toOptionsPatch, ticketOverviewColumns.toOptionsPatch, passwordComplexity.toOptionsPatch]);
    return {
        // General fields
        days, setDays,
        maxChars, setMaxChars,
        calcCutoff, setCalcCutoff,
        ticketClosingDate, setTicketClosingDate,
        activeDate, setActiveDate,
        inactiveDate, setInactiveDate,
        billingStatus, setBillingStatus,
        // Features & Dispositions
        featureCodes, toggleFeature,
        dispositionCodes, toggleDisposition,
        // Advanced option fields
        quicksightDashboard,
        wellTestOverviewColumns,
        meterAllocationConfig,
        wellTestManagementColumns,
        oilInventoryAttributes,
        duplicateTicketBehavior,
        ticketOverviewColumns,
        passwordComplexity,
        networkLinkDefaultProducts: networkLinkDefaultsField.selected,
        toggleNetworkLinkDefaultProduct: networkLinkDefaultsField.toggle,
        // Validation & submit
        errors,
        isInitialized: org !== null && initializedOrganizationId === org.id,
        validate,
        getUpdates,
        reset,
    };
}
